import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Check,
  Clock,
  List,
  RotateCw,
  Search,
  SearchX,
  SlidersHorizontal,
  SquareKanban,
  TriangleAlert,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useMedia } from "@/hooks/use-media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Segmented, SegmentedButtons } from "@/components/ui/segmented";
import { EmptyState, ErrorState, ListSkeleton } from "@/components/ui/states";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import FontiScelta from "@/components/crm/FontiScelta";
import CampoData from "@/components/common/CampoData";
import KanbanClienti, { ordina, prossimo } from "@/components/crm/KanbanClienti";
import OrariLiberi from "@/components/crm/OrariLiberi";
import { erroreDi, giornoDi, oggi, ora, statoCommerciale, valoreBreve } from "@/lib/crm";
import { cn } from "@/lib/utils";

/* Le tre liste di lavoro della D5, più «Tutti». Quello che stava su Notion, ma
   letto dai fatti: un appuntamento PASSATO senza esito resta fra le «Visite senza esito».
   Non si chiama come la colonna «Nuovo appuntamento» perché non è la stessa cosa: qui
   c'è chi ha una visita senza esito (anche con un preventivo già inviato), non chi la
   deve ancora fissare (0041, D9). Il pacchetto la chiama «Da confermare»: vince il codice. */
const LISTE = [
  { id: "tutti", label: "Tutti" },
  { id: "da_confermare", label: "Visite senza esito" },
  { id: "caldi", label: "Caldi" },
  { id: "da_recuperare", label: "Da recuperare" },
];

const VUOTO_PER_LISTA = {
  tutti: null,
  da_confermare: {
    title: "Nessuna visita senza esito",
    description: "Ogni appuntamento in showroom ha il suo esito. Quando arriva una prenotazione nuova, compare qui.",
  },
  caldi: {
    title: "Nessun cliente caldo",
    description: "Qui arriva chi ha ricevuto un preventivo o è tornato per una seconda visita.",
  },
  da_recuperare: {
    title: "Nessuno da recuperare",
    description: "Qui arriva chi non si è presentato all'appuntamento o è stato segnato perso.",
  },
};

const TUTTI = "__tutti__";
const PER_PAGINA = 50;
const IN_CORSA = ["da_confermare", "appuntamento_fatto", "preventivo_inviato", "in_trattativa"];

/* La vista si ricorda per chi la usa (comodità, non stato: se il browser non
   salva, si riparte dall'elenco). L'indirizzo vince, così un link porta dritto al Kanban. */
const CHIAVE_VISTA = "ida.clienti.vista";
function vistaSalvata() {
  try {
    return window.localStorage.getItem(CHIAVE_VISTA) === "kanban" ? "kanban" : "lista";
  } catch {
    return "lista";
  }
}

/** Il colore della prossima cosa da fare: in ritardo arancio, oggi blu, il resto grigio. */
function tonoProssimo(r, p) {
  if (!p) return "text-ink-3";
  if (p.urgente) return "font-semibold text-warning-ink";
  const quando = r.appuntamento_senza_esito_at || r.prossima_azione_at;
  return quando && giornoDi(quando) === oggi() ? "font-semibold text-brand-ink" : "text-ink-2";
}

function Stato({ codice }) {
  const s = statoCommerciale(codice);
  return (
    <span className="inline-flex items-center gap-2 text-ink">
      <span className={cn("size-2 shrink-0 rounded-full", s.punto)} aria-hidden />
      {s.label}
    </span>
  );
}

const VISTE = [
  { id: "kanban", label: "Kanban", Icona: SquareKanban },
  { id: "lista", label: "Elenco", Icona: List },
];

function Cerca({ valore, onCambia, className }) {
  return (
    <div className={cn("relative", className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
      <Input
        type="search"
        value={valore}
        onChange={(e) => onCambia(e.target.value)}
        placeholder="Cerca nome o telefono"
        aria-label="Cerca per nome o telefono"
        className="pl-9 pr-11 [&::-webkit-search-cancel-button]:hidden"
        data-testid="clienti-cerca"
      />
      {valore ? (
        <button
          type="button"
          onClick={() => onCambia("")}
          aria-label="Cancella la ricerca"
          className="absolute right-0.5 top-1/2 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-sm text-ink-2 hover:text-ink md:size-9"
        >
          <X className="size-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}

/* ── Nuovo cliente ───────────────────────────────────────────────────────── */

/* In quale fase entra (0041, D9). Chiuso, No show e Perso non si scelgono: il venduto
   firmato nasce solo dal contratto. */
const FASI_INGRESSO = [
  { codice: "da_confermare", label: "Nuovo appuntamento", aiuto: "Con la data nasce la visita; senza, resta «da fissare»." },
  {
    codice: "appuntamento_fatto",
    label: "Appuntamento fatto",
    aiuto: "È entrato in showroom senza prenotare: nasce la visita di oggi, già fatta.",
  },
  {
    codice: "preventivo_inviato",
    label: "Preventivo inviato",
    aiuto: "Si apre il preventivatore: la carta si sposta quando il preventivo parte.",
  },
  {
    codice: "in_trattativa",
    label: "In trattativa",
    aiuto: "Si apre il preventivatore: dopo l'invio lo porti in «In trattativa» dal Kanban.",
  },
];
const SEDE_MIA = "__mia__";

/** Le cifre del numero come le cerca l'elenco: senza spazi e senza lo «00» davanti al prefisso. */
const cifreDi = (testo) =>
  String(testo || "")
    .replace(/\D/g, "")
    .replace(/^00/, "");
/** Lo stesso numero? Le cifre uguali, a meno del 39 davanti. La ricerca dell'elenco trova anche i pezzi di un
    numero («085 412345» dentro «085 4123456»): l'avviso vale solo per il numero intero, come il 409 del server. */
const senza39 = (c) => (c.length > 10 && c.startsWith("39") ? c.slice(2) : c);
const stessoNumero = (a, b) => senza39(cifreDi(a)) === senza39(cifreDi(b));

/* Il numero c'è già fra i clienti che vedo: un avviso, non un blocco. Arriva in due modi: mentre si
   scrive (l'elenco cercato per cifre, con la stessa RLS) o al salvataggio (il 409 del server, che
   normalizza come il database). La scelta è di chi scrive: aprire la scheda che c'è, o continuare. */
function AvvisoDoppione({ titolo, children, unico, busy, onApri, onContinua }) {
  return (
    <div role="alert" className="flex gap-3 rounded-lg bg-warning-bg px-4 py-3 text-warning-ink" data-testid="nuovo-cliente-doppione">
      <TriangleAlert className="mt-0.5 size-5 shrink-0" aria-hidden />
      <div className="grid min-w-0 gap-inside">
        <p className="font-bold">{titolo}</p>
        <div>{children}</div>
        <div className="mt-micro flex flex-wrap gap-inside">
          {unico ? (
            <Button type="button" onClick={onApri} data-testid="doppione-apri">
              Apri la sua scheda
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={onContinua} disabled={busy} data-testid="doppione-aggiungi">
            {busy ? "Salvo…" : "È un'altra persona, continua"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Nomi({ doppioni, onApri }) {
  return doppioni.map((d, i) => (
    <span key={d.id}>
      {i > 0 ? (i === doppioni.length - 1 ? " e " : ", ") : null}
      <Link
        to={`/clienti/${d.id}`}
        onClick={(e) => {
          // Il dialogo si chiude prima di cambiare pagina, come con «Apri la sua scheda».
          e.preventDefault();
          onApri(d.id);
        }}
        className="font-bold underline underline-offset-2"
      >
        {d.nome}
      </Link>
    </span>
  ));
}

/** Chi è, in una frase: «Preventivo inviato, 34.600 €, la segue Marco Di Nardo». */
function chiE(r) {
  return [
    r.stato_label || statoCommerciale(r.stato).label,
    r.valore !== null && r.valore !== undefined ? valoreBreve(r) : null,
    r.venditore ? `la segue ${r.venditore}` : null,
  ]
    .filter(Boolean)
    .join(", ");
}

function Fase({ attiva, onClick, testId, children }) {
  return (
    <button
      type="button"
      aria-pressed={attiva}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        "min-h-14 rounded-sm border px-3 py-2 text-left text-small font-semibold leading-tight transition-colors duration-150 ease-enter",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
        attiva ? "border-brand bg-brand-subtle font-bold text-brand-ink" : "border-line bg-surface text-ink-2 hover:bg-surface-2",
      )}
    >
      {children}
    </button>
  );
}

/* `iniziale` = { nome, telefono, fonti } già scritti (da un contatto WhatsApp: fonte WhatsApp); `dopoCreazione(cliente)` gira appena il cliente è salvato, prima di ogni altro passo. */
export function NuovoClienteDialog({ open, onOpenChange, onCreato, iniziale, dopoCreazione }) {
  const navigate = useNavigate();
  const { profile } = useAuth() || {};
  const [nome, setNome] = useState("");
  const [telefono, setTelefono] = useState("");
  const [fonti, setFonti] = useState([]);
  const [altro, setAltro] = useState("");
  const [fase, setFase] = useState("da_confermare");
  const [giorno, setGiorno] = useState("");
  const [orario, setOrario] = useState("");
  const [sede, setSede] = useState(SEDE_MIA);
  const [sedi, setSedi] = useState([]);
  const [busy, setBusy] = useState(false);
  const [doppioni, setDoppioni] = useState(null); // dal 409 del salvataggio
  const [verifica, setVerifica] = useState({ stato: "vuoto" }); // mentre si scrive
  const [continua, setContinua] = useState(false); // «È un'altra persona»

  useEffect(() => {
    if (!open) return;
    setNome(iniziale?.nome || "");
    setTelefono(iniziale?.telefono || "");
    setFonti(iniziale?.fonti || []);
    setAltro("");
    setFase("da_confermare");
    setGiorno("");
    setOrario("");
    setSede(profile?.sede_id || SEDE_MIA);
    setDoppioni(null);
    if (!sedi.length)
      api
        .get("/sedi")
        .then((r) => setSedi(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Il numero si controlla mentre si scrive, da nove cifre in su e quando si smette di scrivere. È una
     cortesia: se la lettura non risponde non si dice niente, e al salvataggio resta il controllo del server. */
  useEffect(() => {
    setContinua(false);
    const cifre = cifreDi(telefono);
    if (!open || cifre.length < 9) {
      setVerifica({ stato: "vuoto" });
      return undefined;
    }
    setVerifica({ stato: "attesa" });
    let vivo = true;
    const t = setTimeout(() => {
      api
        .get("/crm/clienti", { params: { q: cifre } })
        .then((r) => {
          if (!vivo) return;
          const trovati = (r.data?.righe || []).filter((x) => stessoNumero(x.telefono, telefono)).slice(0, 3);
          setVerifica(trovati.length ? { stato: "trovato", trovati } : { stato: "libero" });
        })
        .catch(() => vivo && setVerifica({ stato: "vuoto" }));
    }, 400);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [telefono, open]);

  const conVisita = fase === "da_confermare" && giorno;
  const inAttesaDiScelta = verifica.stato === "trovato" && !continua;
  const manca = !nome.trim() || !telefono.trim() || (fonti.includes("altro") && !altro.trim()) || (conVisita && !orario);
  const sedeScelta = sede !== SEDE_MIA && sede !== profile?.sede_id ? sede : null;

  const salva = async (ancheSeDoppione) => {
    setBusy(true);
    try {
      const r = await api.post("/crm/clienti", {
        nome: nome.trim(),
        telefono: telefono.trim(),
        fonti,
        fonte_altro: fonti.includes("altro") ? altro.trim() : null,
        fase,
        ...(conVisita ? { visita_giorno: giorno, visita_ora: orario } : {}),
        ...(fase === "da_confermare" && sedeScelta ? { sede_id: sedeScelta } : {}),
        ...(ancheSeDoppione ? { anche_se_doppione: true } : {}),
      });
      await dopoCreazione?.(r.data);
      /* Il perché vero: se Cal non ha risposto in tempo la visita potrebbe esserci, e rifarla ne fa due.
         Resta finché non lo si chiude: il 28-10 l'avviso giallo è sparito prima che lo si leggesse. */
      if (r.data.visita_errore)
        toast.error(`Cliente aggiunto. ${r.data.visita_errore}`, {
          duration: Infinity,
          action: { label: "Chiudi", onClick: () => {} },
          classNames: { actionButton: "!h-11 sm:!h-8 !px-4 !text-sm" },
        });
      if (fase === "preventivo_inviato" || fase === "in_trattativa") {
        // Il fatto lo fa il preventivo: nasce adesso, col cliente dentro.
        try {
          const p = await api.post("/preventivatore", { cliente_id: r.data.id, livello_ricarico_default: "M5" });
          onOpenChange(false);
          navigate(`/preventivi/preventivatore/${p.data.id}`);
          return;
        } catch (err) {
          toast.error(`Cliente aggiunto, il preventivo no: ${erroreDi(err)}`);
        }
      }
      onCreato(r.data);
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.status === 409 ? err.response.data?.detail : null;
      if (detail?.doppioni?.length) setDoppioni(detail.doppioni);
      else toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  const invia = (e) => {
    e.preventDefault();
    if (manca || inAttesaDiScelta || doppioni) return;
    salva(continua);
  };

  const apri = (id) => {
    onOpenChange(false);
    navigate(`/clienti/${id}`);
  };

  const aiuto = FASI_INGRESSO.find((f) => f.codice === fase)?.aiuto;
  const titolare = profile?.ruolo === "titolare";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nuovo-cliente-modal" className="max-h-[92dvh] overflow-y-auto sm:max-w-[620px]">
        <form onSubmit={invia} className="grid gap-card">
          <DialogHeader>
            <DialogTitle>Nuovo cliente</DialogTitle>
            <DialogDescription>Bastano nome e telefono. Il resto lo completi dopo dalla scheda.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-card">
            <div className="grid gap-group md:grid-cols-2">
              <div className="grid content-start gap-inside">
                <Label htmlFor="nc-nome">Nome e cognome</Label>
                <Input
                  id="nc-nome"
                  autoFocus
                  autoComplete="off"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  data-testid="nuovo-cliente-nome"
                />
              </div>
              <div className="grid content-start gap-inside">
                <Label htmlFor="nc-tel">Telefono</Label>
                <Input
                  id="nc-tel"
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  value={telefono}
                  onChange={(e) => {
                    setTelefono(e.target.value);
                    setDoppioni(null);
                  }}
                  aria-describedby="nc-tel-verifica"
                  className={inAttesaDiScelta || doppioni ? "border-warning" : undefined}
                  data-testid="nuovo-cliente-telefono"
                />
                <p
                  id="nc-tel-verifica"
                  aria-live="polite"
                  className="min-h-4 text-caption font-medium"
                  data-testid="nuovo-cliente-verifica"
                >
                  {verifica.stato === "libero" ? (
                    <span className="inline-flex items-center gap-1 text-success-ink">
                      <Check className="size-3.5" aria-hidden />
                      {titolare ? "Non è in archivio" : "Non è fra i clienti che vedi"}
                    </span>
                  ) : verifica.stato === "attesa" ? (
                    <span className="text-ink-3">Controllo il numero…</span>
                  ) : verifica.stato === "trovato" && continua ? (
                    <span className="text-ink-2">Un'altra persona con lo stesso numero: si aggiunge lo stesso.</span>
                  ) : null}
                </p>
              </div>
            </div>
            {doppioni ? (
              <AvvisoDoppione
                titolo="Questo numero è già in archivio"
                unico={doppioni.length === 1}
                busy={busy}
                onApri={() => apri(doppioni[0].id)}
                onContinua={() => salva(true)}
              >
                {doppioni.length === 1 ? (
                  <p>
                    C'è già <span className="font-bold">{doppioni[0].nome}</span> con questo numero.
                  </p>
                ) : (
                  <p>
                    Con questo numero ci sono già <Nomi doppioni={doppioni} onApri={apri} />.
                  </p>
                )}
              </AvvisoDoppione>
            ) : inAttesaDiScelta ? (
              <AvvisoDoppione
                titolo="Questo numero è già in archivio"
                unico={verifica.trovati.length === 1}
                onApri={() => apri(verifica.trovati[0].id)}
                onContinua={() => setContinua(true)}
              >
                {verifica.trovati.map((r) => (
                  <p key={r.id}>
                    È di <span className="font-bold">{r.nome}</span>: {chiE(r)}.
                  </p>
                ))}
              </AvvisoDoppione>
            ) : null}
            <FontiScelta scelte={fonti} onCambia={setFonti} altro={altro} onAltro={setAltro} />
            <fieldset className="grid gap-inside">
              <legend className="mb-inside text-body-strong text-ink">In che fase entra</legend>
              <div className="grid grid-cols-2 gap-inside md:grid-cols-4" data-testid="nuovo-cliente-fasi">
                {FASI_INGRESSO.map((f) => (
                  <Fase key={f.codice} attiva={fase === f.codice} onClick={() => setFase(f.codice)} testId={`fase-${f.codice}`}>
                    {f.label}
                  </Fase>
                ))}
              </div>
              <p className="text-small text-ink-2" aria-live="polite" data-testid="nuovo-cliente-fase-aiuto">
                {aiuto}
              </p>
            </fieldset>
            {fase === "da_confermare" ? (
              <div className="grid gap-group rounded-lg bg-surface-2 p-4" data-testid="nuovo-cliente-visita">
                {sedi.length > 1 ? (
                  <div className="grid content-start gap-inside">
                    <Label id="nc-sede">Sede</Label>
                    <Segmented
                      aria-label="Sede"
                      value={sede}
                      onValueChange={setSede}
                      options={sedi.map((s) => ({ value: s.id, label: s.nome }))}
                      className="w-full [&>*]:flex-1"
                    />
                  </div>
                ) : null}
                <div className="grid content-start gap-inside">
                  <Label htmlFor="nc-giorno">
                    Visita <span className="font-normal text-ink-3">· facoltativa</span>
                  </Label>
                  <CampoData id="nc-giorno" value={giorno} onChange={(e) => setGiorno(e.target.value)} testId="nuovo-cliente-giorno" />
                </div>
                <OrariLiberi
                  id="nc-ora"
                  testId="nuovo-cliente-ora"
                  giorno={giorno}
                  sedeId={sede === SEDE_MIA ? undefined : sede}
                  valore={orario}
                  onScegli={setOrario}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter className="gap-inside">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <Button type="submit" disabled={busy || manca || Boolean(doppioni) || inAttesaDiScelta} data-testid="nuovo-cliente-salva">
              {busy
                ? "Salvo…"
                : fase === "preventivo_inviato" || fase === "in_trattativa"
                  ? "Salva e prepara il preventivo"
                  : conVisita
                    ? "Salva e fissa la visita"
                    : "Salva"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ── L'elenco ────────────────────────────────────────────────────────────── */

function Schede({ lista, conteggi, onCambia }) {
  return (
    <div role="group" aria-label="Liste" className="-mb-px flex max-w-full gap-1 overflow-x-auto" data-testid="clienti-liste">
      {LISTE.map((l) => {
        const n = conteggi[l.id];
        const attiva = lista === l.id;
        return (
          <button
            key={l.id}
            type="button"
            aria-pressed={attiva}
            onClick={() => onCambia(l.id)}
            data-testid={`lista-${l.id}`}
            className={cn(
              "inline-flex h-11 shrink-0 items-center gap-2 border-b-2 px-3 transition-colors duration-150 ease-enter",
              "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand",
              attiva ? "border-brand font-bold text-ink-strong" : "border-transparent font-medium text-ink-2 hover:text-ink",
            )}
          >
            {l.label}
            {n !== undefined ? (
              <span
                className={cn(
                  "inline-flex h-5 min-w-5 items-center justify-center rounded-xs px-1.5 text-caption tabular-nums",
                  l.id === "da_confermare" && n > 0
                    ? "bg-warning-bg text-warning-ink"
                    : l.id === "da_recuperare" && n > 0
                      ? "bg-danger-bg text-danger-ink"
                      : attiva
                        ? "bg-brand-subtle text-brand-ink"
                        : "bg-surface-3 text-ink-2",
                )}
                data-testid={`conteggio-${l.id}`}
              >
                {n}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function FiltroStato({ stato, onCambia, stati, className }) {
  return (
    <Select value={stato} onValueChange={onCambia}>
      <SelectTrigger className={className} aria-label="Filtra per stato" data-testid="filtro-stato">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={TUTTI}>Stato: tutti</SelectItem>
        {stati.map((s) => (
          <SelectItem key={s.codice} value={s.codice}>
            {s.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FiltroVenditore({ venditore, onCambia, venditori, className }) {
  return (
    <Select value={venditore} onValueChange={onCambia} disabled={venditori.length < 2}>
      <SelectTrigger className={className} aria-label="Filtra per chi lo segue" data-testid="filtro-venditore">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={TUTTI}>Lo segue: tutti</SelectItem>
        {venditori.map((v) => (
          <SelectItem key={v.id} value={v.id}>
            {v.nome}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FiltroOrdine({ ordine, onCambia, className }) {
  return (
    <Select value={ordine} onValueChange={onCambia}>
      <SelectTrigger className={className} aria-label="Ordina" data-testid="filtro-ordine">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="urgenti">Prima i più urgenti</SelectItem>
        <SelectItem value="recenti">Prima i più recenti</SelectItem>
      </SelectContent>
    </Select>
  );
}

function Tabella({ righe, appenaCreato, onApri }) {
  return (
    <Table data-testid="clienti-tabella">
      <TableHeader>
        <TableRow>
          <TableHead className="pl-4">Cliente</TableHead>
          <TableHead>Stato</TableHead>
          <TableHead>Prossima azione</TableHead>
          <TableHead className="text-right">Valore</TableHead>
          <TableHead className="hidden lg:table-cell">Sede</TableHead>
          <TableHead className="hidden pr-4 xl:table-cell">Fonte</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {righe.map((r) => {
          const p = prossimo(r);
          return (
            <TableRow
              key={r.id}
              data-testid={`cliente-row-${r.id}`}
              className={cn("cursor-pointer", appenaCreato === r.id && "bg-brand-subtle")}
              onClick={() => onApri(r.id)}
            >
              <TableCell className="max-w-[18rem] pl-4">
                <Link
                  to={`/clienti/${r.id}`}
                  className="block truncate font-semibold text-ink-strong hover:underline"
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`cliente-link-${r.id}`}
                >
                  {r.nome}
                </Link>
                <span className="block truncate text-small text-ink-2">
                  {[r.telefono, r.venditore].filter(Boolean).join(" · ") || "senza recapito"}
                </span>
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <Stato codice={r.stato} />
              </TableCell>
              <TableCell className={cn("max-w-[16rem] truncate", tonoProssimo(r, p))}>{p ? p.testo : "—"}</TableCell>
              <TableCell className="whitespace-nowrap text-right font-semibold tabular-nums text-ink-strong">{valoreBreve(r)}</TableCell>
              <TableCell className="hidden text-ink-2 lg:table-cell">{r.sede || "—"}</TableCell>
              <TableCell className="hidden max-w-[12rem] truncate pr-4 text-ink-2 xl:table-cell">{r.fonte || "—"}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* A 390 la tabella diventa un elenco: nome, stato con il valore, e la prossima cosa da fare. */
function Elenco({ righe, appenaCreato }) {
  return (
    <ul data-testid="clienti-elenco">
      {righe.map((r) => {
        const p = prossimo(r);
        return (
          <li key={r.id} data-testid={`cliente-row-${r.id}`} className="border-b border-line-subtle last:border-b-0">
            <Link
              to={`/clienti/${r.id}`}
              data-testid={`cliente-link-${r.id}`}
              className={cn(
                "grid min-h-16 gap-0.5 px-4 py-3 transition-colors duration-150 ease-enter hover:bg-surface-2",
                "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand",
                appenaCreato === r.id && "bg-brand-subtle",
              )}
            >
              <span className="truncate font-bold text-ink-strong">{r.nome}</span>
              <span className="flex min-w-0 items-center gap-1.5 text-small text-ink-2">
                <span className={cn("size-2 shrink-0 rounded-full", statoCommerciale(r.stato).punto)} aria-hidden />
                <span className="truncate">
                  {statoCommerciale(r.stato).label}
                  {r.valore !== null && r.valore !== undefined ? ` · ${valoreBreve(r)}` : ""}
                </span>
              </span>
              {p ? <span className={cn("truncate text-small", tonoProssimo(r, p))}>{p.testo}</span> : null}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

export default function ClientiPage() {
  const nav = useNavigate();
  const largo = useMedia("(min-width: 768px)");
  const [params, setParams] = useSearchParams();
  const lista = LISTE.some((l) => l.id === params.get("lista")) ? params.get("lista") : "tutti";
  const vista = ["lista", "kanban"].includes(params.get("vista")) ? params.get("vista") : vistaSalvata();
  const [q, setQ] = useState(params.get("q") || "");
  const [qAttiva, setQAttiva] = useState((params.get("q") || "").trim());
  const [stato, setStato] = useState(TUTTI);
  const [venditore, setVenditore] = useState(TUTTI);
  const [ordine, setOrdine] = useState("urgenti");
  const [soloRitardo, setSoloRitardo] = useState(false);
  const [filtriAperti, setFiltriAperti] = useState(false);
  const [quanti, setQuanti] = useState(PER_PAGINA);
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [aggiornato, setAggiornato] = useState(null);
  const [aperto, setAperto] = useState(false);
  const [iniziale, setIniziale] = useState(null);
  const [appenaCreato, setAppenaCreato] = useState(null);
  const richiesta = useRef(0);

  /* La ricerca parte quando smetti di scrivere, non a ogni lettera. */
  useEffect(() => {
    const t = setTimeout(() => setQAttiva(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const carica = useCallback(() => {
    const mia = ++richiesta.current;
    const query = { lista };
    if (qAttiva) query.q = qAttiva;
    // Nel Kanban le colonne SONO gli stati: il filtro per stato lì non vale.
    if (stato !== TUTTI && vista === "lista") query.stato = stato;
    if (venditore !== TUTTI) query.venditore = venditore;
    api
      .get("/crm/clienti", { params: query })
      .then((r) => {
        if (mia === richiesta.current) {
          setDati(r.data);
          setErrore(null);
          setAggiornato(new Date().toISOString());
        }
      })
      .catch(
        (e) =>
          mia === richiesta.current &&
          setErrore({ testo: erroreDi(e), codice: e?.response?.status || "NET", alle: new Date().toISOString() }),
      );
  }, [lista, qAttiva, stato, venditore, vista]);

  useEffect(carica, [carica]);
  useEffect(() => setQuanti(PER_PAGINA), [lista, qAttiva, stato, venditore, ordine]);

  /* «Nuovo › Cliente» dal guscio arriva con ?nuovo=1: apre il dialogo di sempre e toglie il parametro. */
  useEffect(() => {
    if (params.get("nuovo") !== "1") return;
    setIniziale(null);
    setAperto(true);
    const p = new URLSearchParams(params);
    p.delete("nuovo");
    setParams(p, { replace: true });
  }, [params, setParams]);

  const cambiaLista = (id) => {
    const nuovi = new URLSearchParams(params);
    if (id === "tutti") nuovi.delete("lista");
    else nuovi.set("lista", id);
    setParams(nuovi, { replace: true });
  };

  const cambiaVista = (id) => {
    try {
      window.localStorage.setItem(CHIAVE_VISTA, id);
    } catch {
      /* senza memoria locale la scelta vale solo per questo indirizzo */
    }
    const nuovi = new URLSearchParams(params);
    nuovi.set("vista", id);
    setParams(nuovi, { replace: true });
  };

  const nuovo = (dati0 = null) => {
    setIniziale(dati0);
    setAperto(true);
  };

  const creato = (c) => {
    setAppenaCreato(c.id);
    /* Con la visita non fissata parla già l'errore, che resta: un verde sopra lo coprirebbe. */
    if (!c.visita_errore)
      toast.success("Cliente aggiunto", {
        action: { label: "Apri la scheda", onClick: () => nav(`/clienti/${c.id}`) },
      });
    if (lista !== "tutti") cambiaLista("tutti");
    else carica();
  };

  const righe = useMemo(() => {
    const tutte = dati?.righe || [];
    return ordine === "urgenti" ? ordina(tutte) : tutte;
  }, [dati, ordine]);
  const conteggi = dati?.conteggi || {};
  const filtri = Boolean(qAttiva) || (stato !== TUTTI && vista === "lista") || venditore !== TUTTI;
  const vuotoLista = VUOTO_PER_LISTA[lista];
  const perKanban = soloRitardo ? righe.filter((r) => prossimo(r)?.urgente) : righe;
  const inCorsa = righe.filter((r) => IN_CORSA.includes(r.stato)).length;
  const soloCifre = /^[\d\s+]+$/.test(qAttiva);

  const sottotitolo =
    dati === null && !errore
      ? "Carico i clienti…"
      : errore && aggiornato
        ? `Ultimo aggiornamento riuscito alle ${ora(aggiornato)}`
        : errore
          ? "Non riusciamo a caricare i clienti"
          : lista === "tutti"
            ? `${inCorsa} in corsa · ${conteggi.tutti ?? righe.length} in archivio`
            : `${righe.length} in «${LISTE.find((l) => l.id === lista).label}» · ${conteggi.tutti ?? 0} in archivio`;

  const togliFiltri = () => {
    setQ("");
    setStato(TUTTI);
    setVenditore(TUTTI);
  };

  return (
    <section data-testid="page-clienti" className="grid grid-cols-[minmax(0,1fr)] gap-card">
      <header className="flex flex-col gap-group lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-h1-mobile text-ink-strong md:text-h1">Clienti</h1>
          <p className="mt-micro text-body text-ink-2" data-testid="clienti-sottotitolo">
            {sottotitolo}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-inside">
          <Cerca valore={q} onCambia={setQ} className="w-full md:w-72" />
          <SegmentedButtons
            voci={VISTE}
            valore={vista}
            onCambia={cambiaVista}
            prefisso="vista"
            aria-label="Vista"
            data-testid="clienti-vista"
          />
          <Button onClick={() => nuovo()} data-testid="btn-nuovo-cliente" className="max-md:flex-1">
            <UserPlus />
            Nuovo cliente
          </Button>
        </div>
      </header>
      <NuovoClienteDialog open={aperto} onOpenChange={setAperto} onCreato={creato} iniziale={iniziale} />

      {vista === "lista" ? (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-group border-b border-line md:flex md:items-end md:justify-between">
          <Schede lista={lista} conteggi={conteggi} onCambia={cambiaLista} />
          {largo ? (
            <div className="flex flex-wrap items-center gap-inside pb-2">
              <FiltroStato stato={stato} onCambia={setStato} stati={dati?.stati || []} className="h-9 w-auto min-w-[9rem]" />
              <FiltroVenditore
                venditore={venditore}
                onCambia={setVenditore}
                venditori={dati?.venditori || []}
                className="h-9 w-auto min-w-[9rem]"
              />
              <FiltroOrdine ordine={ordine} onCambia={setOrdine} className="h-9 w-auto" />
            </div>
          ) : (
            <div className="grid gap-inside pb-3">
              <div className="flex items-center justify-between gap-inside">
                <Button
                  type="button"
                  variant="outline"
                  aria-expanded={filtriAperti}
                  onClick={() => setFiltriAperti((v) => !v)}
                  data-testid="clienti-filtri"
                >
                  <SlidersHorizontal />
                  Filtri
                </Button>
                <FiltroOrdine
                  ordine={ordine}
                  onCambia={setOrdine}
                  className="w-auto border-0 bg-transparent font-semibold text-brand-ink"
                />
              </div>
              {filtriAperti ? (
                <div className="grid gap-inside">
                  <FiltroStato stato={stato} onCambia={setStato} stati={dati?.stati || []} />
                  <FiltroVenditore venditore={venditore} onCambia={setVenditore} venditori={dati?.venditori || []} />
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-group">
          <div className="border-b border-line">
            <Schede lista={lista} conteggi={conteggi} onCambia={cambiaLista} />
          </div>
          <div className="flex flex-wrap items-center gap-inside">
            <FiltroVenditore
              venditore={venditore}
              onCambia={setVenditore}
              venditori={dati?.venditori || []}
              className="w-auto min-w-[9rem] md:h-9"
            />
            <Button
              type="button"
              variant="outline"
              aria-pressed={soloRitardo}
              onClick={() => setSoloRitardo((v) => !v)}
              className={cn("md:h-9", soloRitardo && "border-brand bg-brand-subtle text-brand-ink")}
              data-testid="kanban-solo-ritardo"
            >
              <Clock />
              Solo in ritardo
            </Button>
          </div>
        </div>
      )}

      {errore ? (
        <div className="rounded-xl border border-line bg-surface" data-testid="clienti-errore">
          <ErrorState
            title="Non riusciamo a caricare i clienti"
            code={`CLI-${errore.codice} · ${ora(errore.alle)}`}
            className="py-section"
            actions={
              <Button onClick={carica} data-testid="clienti-riprova">
                <RotateCw />
                Riprova
              </Button>
            }
          >
            {errore.codice === "NET"
              ? "Il gestionale non ha ricevuto risposta. I dati sono al sicuro: controlla la connessione e riprova."
              : `${errore.testo}. I dati sono al sicuro: riprova fra poco.`}
          </ErrorState>
        </div>
      ) : dati === null ? (
        <div className="overflow-hidden rounded-xl border border-line bg-surface" aria-busy="true">
          <ListSkeleton rows={6} label="Carico i clienti…" />
        </div>
      ) : righe.length === 0 ? (
        <div className="rounded-xl border border-line bg-surface">
          {qAttiva ? (
            <EmptyState
              icon={SearchX}
              tone="brand"
              className="py-section"
              title={soloCifre ? `Nessun cliente col numero «${qAttiva}»` : `Nessun cliente si chiama «${qAttiva}»`}
              actions={
                <>
                  <Button variant="outline" onClick={togliFiltri}>
                    Cancella la ricerca
                  </Button>
                  <Button
                    onClick={() => nuovo(soloCifre ? { telefono: qAttiva } : { nome: qAttiva })}
                    data-testid="clienti-vuoto-inserisci"
                  >
                    <UserPlus />
                    Inserisci «{qAttiva}» come nuovo cliente
                  </Button>
                </>
              }
            >
              <span data-testid="clienti-vuoto-filtri">
                {soloCifre
                  ? "Controlla le cifre, o cerca per nome. Se è una persona nuova, inseriscila adesso."
                  : "Controlla come è scritto o cerca il numero di telefono: lo trovi anche se manca il nome. Se è una persona nuova, inseriscila adesso."}
              </span>
            </EmptyState>
          ) : filtri ? (
            <EmptyState
              icon={SearchX}
              tone="brand"
              className="py-section"
              title="Nessun cliente con questi filtri"
              actions={
                <Button variant="outline" onClick={togliFiltri}>
                  Togli i filtri
                </Button>
              }
            >
              <span data-testid="clienti-vuoto-filtri">Prova a togliere un filtro, o a cercare solo una parte del nome o del numero.</span>
            </EmptyState>
          ) : vuotoLista ? (
            <EmptyState icon={Users} tone="success" className="py-section" title={vuotoLista.title}>
              <span data-testid={`clienti-vuoto-${lista}`}>{vuotoLista.description}</span>
            </EmptyState>
          ) : (
            <EmptyState
              icon={Users}
              tone="brand"
              className="py-section"
              title="Nessun cliente ancora"
              actions={
                <Button onClick={() => nuovo()}>
                  <UserPlus />
                  Nuovo cliente
                </Button>
              }
            >
              <span data-testid="clienti-vuoto">
                Il primo lo aggiungi da qui con nome e telefono. Chi prenota dalla landing entra da solo.
              </span>
            </EmptyState>
          )}
        </div>
      ) : vista === "kanban" ? (
        <KanbanClienti righe={perKanban} onCambiato={carica} />
      ) : (
        <div className="overflow-hidden rounded-xl border border-line bg-surface">
          {largo ? (
            <Tabella righe={righe.slice(0, quanti)} appenaCreato={appenaCreato} onApri={(id) => nav(`/clienti/${id}`)} />
          ) : (
            <Elenco righe={righe.slice(0, quanti)} appenaCreato={appenaCreato} />
          )}
          <div className="flex flex-col gap-inside border-t border-line-subtle px-4 py-3 text-ink-2 md:flex-row md:items-center md:justify-between">
            <span className="tabular-nums" data-testid="clienti-mostrati">
              Mostrati {Math.min(quanti, righe.length)} di {righe.length}
            </span>
            {righe.length > quanti ? (
              <Button variant="outline" onClick={() => setQuanti((n) => n + PER_PAGINA)} data-testid="clienti-altri">
                Carica altri {Math.min(PER_PAGINA, righe.length - quanti)}
              </Button>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
}
